import React, { useEffect, useRef } from "react";
import ReactJson from "react18-json-view";
import "react18-json-view/src/style.css";
import { useTermSelection } from "../../../hooks/useTermSelection";

type JSONDisplayProps = {
  data: any;
  searchQuery?: string;
  highlightDuration?: number;
  isCurrentSearchResult?: boolean;
  currentOccurrenceIndex?: number | undefined;
  onTermSelect?: (term: string) => void;
};

const JSONDisplay: React.FC<JSONDisplayProps> = ({
  data,
  searchQuery,
  highlightDuration = 2000,
  isCurrentSearchResult = false,
  currentOccurrenceIndex,
  onTermSelect,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const highlightedElementsRef = useRef<Element[]>([]);

  const { handleDoubleClickEvent, handleClick } = useTermSelection({
    onTermSelect,
  });

  useEffect(() => {
    highlightedElementsRef.current.forEach((el) => {
      el.classList.remove("search-highlight");
      el.classList.remove("search-highlight-current");
    });
    highlightedElementsRef.current = [];

    if (!searchQuery || !containerRef.current) {
      return;
    }

    const timer = setTimeout(() => {
      const query = searchQuery.toLowerCase();
      const container = containerRef.current;
      if (!container) return;

      const walker = document.createTreeWalker(
        container,
        NodeFilter.SHOW_TEXT,
        null
      );

      const matchingElements: Element[] = [];
      let node: Node | null;
      let matchIndex = 0;

      while ((node = walker.nextNode())) {
        const textContent = node.textContent?.toLowerCase() || "";
        if (textContent.includes(query) && node.parentElement) {
          matchingElements.push(node.parentElement);
          // this is for identifying if an object is highlighted yellow (current) or blue (next search result), and give the appropriate css class
          const isThisOccurrence =
            isCurrentSearchResult && currentOccurrenceIndex === matchIndex;
          const highlightClass = isThisOccurrence
            ? "search-highlight-current"
            : "search-highlight";
          node.parentElement.classList.add(highlightClass);
          matchIndex++;
        }
      }

      highlightedElementsRef.current = matchingElements;

      if (
        isCurrentSearchResult &&
        currentOccurrenceIndex !== undefined &&
        matchingElements[currentOccurrenceIndex]
      ) {
        matchingElements[currentOccurrenceIndex]?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      } else if (matchingElements.length > 0) {
        matchingElements[0]?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    }, 100);

    return () => {
      clearTimeout(timer);
    };
  }, [
    searchQuery,
    data,
    highlightDuration,
    isCurrentSearchResult,
    currentOccurrenceIndex,
  ]);

  return (
    <div className="bg-white shadow-[0_2px_8px_rgba(0,0,0,0.7)] flex flex-col h-full flex-grow">
      <div
        ref={containerRef}
        className="flex-grow overflow-y-auto p-4"
        onClick={handleClick}
        onDoubleClick={handleDoubleClickEvent}
      >
        <ReactJson
          src={data}
          theme="github"
          collapsed={false}
          enableClipboard={false}
          style={{
            width: "100%",
            backgroundColor: "transparent",
            fontFamily: "inherit",
          }}
        />
      </div>
    </div>
  );
};

export default JSONDisplay;
